/**
 * On-screen keyboard inset.
 *
 * A mobile browser does not shrink the layout viewport for the soft keyboard, so
 * `100dvh` keeps its full height and the bottom of a form — the submit button,
 * the last field — ends up behind the keyboard with no way to scroll it into
 * view. The layout has to be told how much is covered.
 *
 * The visible region is read from `visualViewport`, the only API that reports
 * the keyboard: `window.innerHeight` is unchanged by it. The covered height is
 * the difference between the tallest viewport seen with the keyboard closed and
 * the current one.
 *
 * `visualViewport.offsetTop` is deliberately *not* part of that sum. It reports
 * how far the browser has panned the page, and on iOS focusing a field pans by
 * hundreds of points to bring it up. Subtracting it would under-report the
 * keyboard and, once the pan exceeds the keyboard's own height, report a closed
 * keyboard while the keyboard is plainly open — which is the exact bug this
 * replaces.
 *
 * On native this reports a closed keyboard, because React Native's own
 * `KeyboardAvoidingView` handles the equivalent there.
 */

import { useEffect, useRef, useState } from 'react';

/** Below this, a difference is browser chrome moving, not a keyboard. */
const KEYBOARD_THRESHOLD = 80;

export interface KeyboardInset {
  /** Height in points covered by the keyboard; zero when it is closed. */
  height: number;
  /** True while the keyboard covers part of the layout viewport. */
  open: boolean;
  /** Height in points of the region the user can actually see. */
  visibleHeight: number;
}

const CLOSED: KeyboardInset = { height: 0, open: false, visibleHeight: 0 };

/**
 * Tracks how much of the viewport the on-screen keyboard covers.
 *
 * @returns The covered height, whether the keyboard is open, and the visible height.
 */
export function useKeyboardInset(): KeyboardInset {
  const [inset, setInset] = useState<KeyboardInset>(CLOSED);

  /** The visible height with no keyboard, which the covered height is measured against. */
  const baseline = useRef(0);
  /** The layout viewport height with no keyboard, which distinguishes the regimes. */
  const layoutBaseline = useRef(0);
  /** Used to tell a rotation from a keyboard: only one of them changes the width. */
  const width = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    baseline.current = Math.round(viewport.height);
    layoutBaseline.current = Math.round(window.innerHeight);
    width.current = Math.round(viewport.width);

    const update = () => {
      const visible = Math.round(viewport.height);
      const layout = Math.round(window.innerHeight);
      const currentWidth = Math.round(viewport.width);

      // A keyboard never changes the width, so a width change is a rotation or a
      // window resize: re-measure rather than reading the new height as a keyboard.
      if (currentWidth !== width.current) {
        width.current = currentWidth;
        baseline.current = visible;
        layoutBaseline.current = layout;
        setInset({ ...CLOSED, visibleHeight: visible });
        return;
      }

      // Two things can happen when the keyboard opens, and they need opposite
      // handling. Either it covers the page — Safari, the layout viewport is
      // untouched — or it resizes the page, which Chrome does for
      // `interactive-widget=resizes-content`. In the second case `dvh`, `inset-0`
      // and every scroll view already stop where the keyboard begins, so the
      // layout viewport shrinks and there is nothing left to compensate for.
      // Adding the inset anyway crushes the content: the container loses the
      // keyboard's height a second time and pushes everything off the top.
      if (layout <= layoutBaseline.current - KEYBOARD_THRESHOLD) {
        setInset({ ...CLOSED, visibleHeight: visible });
        return;
      }

      const covered = layoutBaseline.current - visible;

      if (covered > KEYBOARD_THRESHOLD) {
        setInset({ height: covered, open: true, visibleHeight: visible });
        return;
      }

      // Closed: the current height is the new baseline, so browser chrome moving
      // does not accumulate into a phantom keyboard.
      baseline.current = visible;
      layoutBaseline.current = layout;
      setInset({ ...CLOSED, visibleHeight: visible });
    };

    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    update();

    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}

/**
 * Keeps the focused field in front of the keyboard.
 *
 * The browser scrolls a focused field into view against the *old* viewport, so
 * by the time the keyboard has finished animating the field can be underneath
 * it. Re-centring on the shrunken viewport is what actually puts it back. The
 * listener covers moving between fields while the keyboard is already open,
 * where the inset itself does not change.
 *
 * @param open Whether the keyboard is currently open.
 * @param height The covered height; the effect re-runs as the inset settles.
 */
export function useRevealFocusedField(open: boolean, height: number): void {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;

    const reveal = () => {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) return;
      if (!active.matches('input, textarea, select, [contenteditable="true"]')) return;

      active.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };

    // The keyboard animates in, so the first attempt waits for it to settle.
    const timer = setTimeout(reveal, 50);
    document.addEventListener('focusin', reveal);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('focusin', reveal);
    };
  }, [open, height]);
}
